import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { API_BASE_URL } from '../../environments/environment';
import { Router } from '@angular/router';
import { MobileZoomService } from '../common/mobile-zoom/mobile-zoom.service';
import { MatTableDataSource, MatPaginator } from '@angular/material';
import { getAreaSession, saveAreaSession } from '../common/utils';
import moment from 'moment';
import * as XLSX from 'xlsx';

// 一覧の1行（物件ごと）
export interface Vacancy {
  nameSite: string;
  cdSite: string;
  endDateContract: string;
  frames: number;
  weekTimes: number;
  monthTimes: number;
  // 祝日のみ（区分ありで不足があり、祝日・全社休日の区分を見なければ不足が無い）
  holidayOnlyWeek: boolean;
  holidayOnlyMonth: boolean;
  staffVacancy: string;
  siteVacancy: string;
  totalSize: number;
}

/**
 * 欠員確認
 * 今月の契約時間と雇用指示の時間を日ごとに比べ、物件ごとに欠員コマ・欠員時間を出す（見るだけ）
 * 祝日・全社休日の勤務の区分も見る（契約が祝日勤務ありで雇用指示が祝日休みなら不足）
 * 欠員時間（月）は今月の不足の合計、欠員コマ・欠員時間（週）は月末7日間だけ
 */
@Component({
  selector: 'app-vacancy',
  templateUrl: './vacancy.component.html',
  styleUrls: ['./vacancy.component.less']
})
export class VacancyComponent implements OnInit, OnDestroy {

  // カラム名
  public columnDefs = [
    'nameSite',
    'cdSite',
    'status',
    'frames',
    'weekTimes',
    'monthTimes'
  ];

  // テーブルデータソース
  vacancyList = new MatTableDataSource<Vacancy>();

  // エクスポート用のデータ
  exportData = [];

  // 欠員コマ・欠員時間（週）の対象（月末7日間）
  days = '';

  // 欠員時間（月）の対象（今月）
  monthDays = `(${moment().startOf('month').format('YYYY/MM/DD')}~${moment().endOf('month').format('YYYY/MM/DD')})`;

  // 物件名絞込み
  siteName = '';

  // エリア絞り込み
  areas = [];

  // 選択されたエリア
  areaIds = [];

  // 欠員状態絞り込み
  status = '全て';

  // 欠員状態絞り込みリスト
  public statusChoices = [
    {value: '全て'},
    {value: '清掃員なし'},
    {value: '清掃員いるが時間不足'}
  ];

  // ローディング中：true　終了：false
  nowLoading = true;
  loadingExport = false;

  // データ数
  dataSourceLength = 0;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  public headers = new HttpHeaders({
    'X-LoginAccessKey': localStorage.getItem('accessKey')
  });

  constructor(private http: HttpClient, private router: Router, public mz: MobileZoomService) { }

  /**
   * ほかの画面へ移るとき：スマホの viewport を元に戻す
   */
  ngOnDestroy() {
    this.mz.leave();
  }

  ngOnInit() {
    // スマホ：PC と同じ見た目を縮めて出す（MobileZoomService）
    this.mz.enter();

    const end = moment().endOf('month');
    this.days = `(${moment(end).add(-6, 'days').format('YYYY/MM/DD')}~${end.format('YYYY/MM/DD')})`;

    // 前回の検索条件
    this.siteName = localStorage.getItem('vacancySiteName') || '';
    this.status = localStorage.getItem('vacancyStatus') || '全て';
    this.getAreas();
  }

  ngAfterViewInit() {
    this.vacancyList.paginator = this.paginator;
    this.getData(this.paginator.pageIndex, this.paginator.pageSize);
    this.paginator.page.subscribe(() => {
      this.getData(this.paginator.pageIndex, this.paginator.pageSize);
    });
  }

  /**
   * 欠員物件データ取得
   * API: recruit/vacancy
   */
  getData(index: number, size: number) {
    this.nowLoading = true;
    localStorage.setItem('vacancySiteName', this.siteName);
    localStorage.setItem('vacancyStatus', this.status);

    this.http.get(
      API_BASE_URL + '/api/v1/recruit/vacancy?index=' + index
      + '&size=' + size
      + '&siteName=' + encodeURIComponent(this.siteName)
      + '&areaIds=' + (this.areaIds || []).join(',')
      + '&status=' + encodeURIComponent(this.status),
      {headers: this.headers}
    )
    .subscribe(res => {
      const data = res as Vacancy[];
      this.vacancyList = new MatTableDataSource(data);
      this.dataSourceLength = data.length > 0 ? data[0].totalSize : 0;
      this.nowLoading = false;
    },
    error => {
      alert('認証に失敗しました');
      this.router.navigate(['/']);
      this.nowLoading = false;
    });
  }

  /**
   * エリアドロップダウン作成
   */
  getAreas() {
    const area = getAreaSession();

    if (area && area.length) {
      this.areas = area;
    } else {
      this.http.get(
        API_BASE_URL + '/api/v1/area', {headers: this.headers}
      ).subscribe(res => {
        this.areas = res as any[];

        // セッション保存
        saveAreaSession(this.areas);
      }, error => {
        alert('認証に失敗しました');
        this.router.navigate(['/']);
      });
    }
  }

  /**
   * 検索機能イベント
   * @param event エリアドロップダウン表示：true、閉じる：false
   */
  applyFilter(event?: boolean) {
    if (event) {
      return;
    }
    this.paginator.pageIndex = 0;
    this.getData(this.paginator.pageIndex, this.paginator.pageSize);
  }

  /**
   * エクスポート用に全件取得
   * API: recruit/allVacancy
   */
  downloadBtn() {
    this.loadingExport = true;
    this.exportData = [];

    this.http.get(
      API_BASE_URL + '/api/v1/recruit/allVacancy'
      + '?siteName=' + encodeURIComponent(this.siteName)
      + '&areaIds=' + (this.areaIds || []).join(',')
      + '&status=' + encodeURIComponent(this.status),
      {headers: this.headers}
    )
    .subscribe(res => {
      const data = res as Vacancy[];
      for (const d of data) {
        let status = '';
        if (d.staffVacancy) {
          status = d.staffVacancy;
        }
        if (d.siteVacancy) {
          if (status) {
            status += '/';
          }
          status += d.siteVacancy;
        }
        this.exportData.push({
            '物件名': d.nameSite
          , '物件コード': d.cdSite
          , '欠員状態': status
          , '欠員コマ': d.frames
          , '欠員時間（週）': Number(d.weekTimes)
          , '欠員時間（月）': Number(d.monthTimes)
          , '祝日のみ': this.isHolidayOnly(d, d.holidayOnlyMonth) ? '祝日のみ' : ''
          , '契約終了日': d.endDateContract
        });
      }
      this.downloadCsv();
      this.loadingExport = false;
    },
    error => {
      alert('認証に失敗しました');
      this.router.navigate(['/']);
      this.loadingExport = false;
    });
  }

  /**
   * エクスポートボタン
   */
  downloadCsv() {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.exportData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    // ファイル名設定（月末7日間。ファイル名に「/」は使えないので yyyyMMdd）
    const end = moment().endOf('month');
    const fileName = '欠員確認(' + moment(end).add(-6, 'days').format('YYYYMMDD') + '-' + end.format('YYYYMMDD') + ').xlsx';
    XLSX.writeFile(wb, fileName);
  }

  /**
   * 時間の表示（小数2桁まで。末尾の 0 は出さない：3 → 3、3.50 → 3.5）
   */
  hourText(h: number): string {
    if (h === null || h === undefined) {
      return '';
    }
    return String(Number(Number(h).toFixed(2)));
  }

  /**
   * 「祝日のみ」の札を出すか（欠員時間の下）
   * 清掃員はいるが、契約の祝日勤務（FULL・時間）に対して雇用指示が祝日休みのため、
   * 祝日・全社休日の区分を見なければ不足が無い（雇用指示の月〜日の勤務を比べても気付けない）とき
   * 判定は SQL（holiday_only_month・holiday_only_week）。清掃員がいない契約の不足は毎日出るので、札の対象にしない
   * @param row 行
   * @param holidayOnly SQL の判定（月か週）
   */
  isHolidayOnly(row: Vacancy, holidayOnly: boolean): boolean {
    return !!row.staffVacancy && !!holidayOnly;
  }

  /**
   * 背景色設定
   * @param index 番号
   */
  setBgColor(index: number) {
    if (index % 2 === 0) {
      return 'blue-contract-bg';
    }
  }

  /**
   * 物件名クリック時に物件詳細情報へ遷移
   * @param row 対象データ
   */
  cellClicked(row: Vacancy) {
    this.router.navigate(['/buildings/' + row.cdSite]);
  }
}
